import { useEffect, useMemo, useRef, useState } from "react";
import { api } from "@/lib/api";
import { useUndoDelete } from "@/lib/useUndoDelete";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { ADMIN } from "@/constants/testIds";
import { Trash2, Upload, Download, FlaskConical, Loader2 } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import ListFilter from "@/components/admin/ListFilter";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

function GlossarySettingsCard() {
  const [s, setS] = useState(null);
  const [saving, setSaving] = useState(false);
  const load = () => api.get("/site-settings/glossary").then(r => setS(r.data));
  useEffect(() => { load(); }, []);
  const put = async (next) => {
    setS(next);
    setSaving(true);
    try { await api.put("/site-settings/glossary", next); }
    catch (e) { toast.error(e.response?.data?.detail || "Save failed"); load(); }
    finally { setSaving(false); }
  };
  const set = (k, v) => put({ ...s, [k]: v });
  const setNum = (k, v, min, max) => {
    const n = Math.max(min, Math.min(max, parseInt(v || "0", 10) || 0));
    put({ ...s, [k]: n });
  };
  if (!s) return null;
  return (
    <Card className="border-slate-200 mt-6">
      <CardHeader>
        <CardTitle className="font-heading flex items-center justify-between">
          <span>Auto-link settings</span>
          <span className="text-xs uppercase tracking-widest text-slate-400 font-normal">
            {saving ? "Saving…" : "Auto-saves"}
          </span>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-5">
        <p className="text-sm text-slate-600">
          Control how the glossary automatically links terms inside blog content. Changes apply the next time a reader loads a blog — no reprocessing needed.
        </p>

        <label className="flex items-center gap-3 cursor-pointer">
          <input type="checkbox" data-testid="glossary-enabled"
                 checked={s.enabled} onChange={e => set("enabled", e.target.checked)}
                 className="w-4 h-4 accent-sky-700"/>
          <span className="text-sm text-slate-800">
            <strong>Enable auto-linking</strong>
            <span className="block text-xs text-slate-500">When off, blogs render exactly as written — no glossary highlights.</span>
          </span>
        </label>

        <div className="grid md:grid-cols-3 gap-4">
          <div>
            <label className="block text-xs uppercase tracking-widest text-slate-500 mb-1">
              Max links per term (per blog)
            </label>
            <Input data-testid="glossary-max-per-blog" type="number" min={0} max={100}
                   value={s.max_per_blog}
                   onChange={e => setNum("max_per_blog", e.target.value, 0, 100)}/>
            <p className="text-xs text-slate-400 mt-1">
              How many times the same term is highlighted inside one blog. <strong>0 = unlimited.</strong>
            </p>
          </div>
          <div>
            <label className="block text-xs uppercase tracking-widest text-slate-500 mb-1">
              Max links per term (per section)
            </label>
            <Input data-testid="glossary-max-per-section" type="number" min={0} max={100}
                   value={s.max_per_section}
                   onChange={e => setNum("max_per_section", e.target.value, 0, 100)}/>
            <p className="text-xs text-slate-400 mt-1">
              Additional cap per section (a blog section = one heading). <strong>0 = no per-section cap.</strong>
            </p>
          </div>
          <div>
            <label className="block text-xs uppercase tracking-widest text-slate-500 mb-1">
              Minimum term length
            </label>
            <Input data-testid="glossary-min-length" type="number" min={2} max={20}
                   value={s.min_length}
                   onChange={e => setNum("min_length", e.target.value, 2, 20)}/>
            <p className="text-xs text-slate-400 mt-1">Ignore terms shorter than this many characters.</p>
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-4">
          <label className="flex items-start gap-3 cursor-pointer">
            <input type="checkbox" data-testid="glossary-case-sensitive"
                   checked={s.case_sensitive} onChange={e => set("case_sensitive", e.target.checked)}
                   className="w-4 h-4 accent-sky-700 mt-0.5"/>
            <span className="text-sm text-slate-800">
              <strong>Case-sensitive matching</strong>
              <span className="block text-xs text-slate-500">
                Off (default): &quot;hospice&quot; matches &quot;Hospice&quot; and &quot;HOSPICE&quot;. On: only exact case.
              </span>
            </span>
          </label>
          <label className="flex items-start gap-3 cursor-pointer">
            <input type="checkbox" data-testid="glossary-match-aliases"
                   checked={s.match_aliases} onChange={e => set("match_aliases", e.target.checked)}
                   className="w-4 h-4 accent-sky-700 mt-0.5"/>
            <span className="text-sm text-slate-800">
              <strong>Also match aliases</strong>
              <span className="block text-xs text-slate-500">
                When on, alternates listed on each term (e.g. &quot;DNR&quot; for &quot;Do Not Resuscitate&quot;) are highlighted too.
              </span>
            </span>
          </label>
        </div>
      </CardContent>
    </Card>
  );
}

/**
 * Compact bar rendered above the glossary term list. Mirrors the test-users
 * and test-blogs flows — click loads a preview of terms classified as test
 * fixtures (CsvAutolink*, HoverAlpha*, ImportedAlpha*, MergedTerm*, etc.),
 * confirm hard-deletes them. Hidden entirely when nothing matches.
 */
function RemoveTestGlossaryBar({ onDeleted }) {
  const [preview, setPreview] = useState(null);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [count, setCount] = useState(0);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await api.get("/admin/test-glossary");
        if (!cancelled) setCount(r.data?.count || 0);
      } catch (_) { /* silent */ }
    })();
    return () => { cancelled = true; };
  }, [onDeleted]);

  if (count === 0) return null;

  const openConfirm = async () => {
    try {
      const r = await api.get("/admin/test-glossary");
      setPreview(r.data);
      setOpen(true);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't load test-glossary preview");
    }
  };

  const doDelete = async () => {
    setBusy(true);
    try {
      const r = await api.delete("/admin/test-glossary");
      const n = r.data?.deleted_terms || 0;
      toast.success(n ? `Removed ${n} test glossary term${n === 1 ? "" : "s"}.` : "No test terms matched.");
      setOpen(false);
      setCount(0);
      onDeleted?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't remove test glossary terms");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="remove-test-glossary-bar" className="mb-4 flex items-center justify-between gap-3 px-3 py-2 rounded-md border border-red-200 dark:border-red-900 bg-red-50/50 dark:bg-red-950/20">
      <div className="text-xs text-red-800 dark:text-red-300 inline-flex items-center gap-2">
        <FlaskConical className="w-3.5 h-3.5"/>
        {count} test glossary term{count === 1 ? " is" : "s are"} leftover from prior test runs.
      </div>
      <Button
        data-testid="remove-test-glossary-btn"
        onClick={openConfirm}
        variant="outline"
        size="sm"
        className="border-red-300 text-red-700 hover:bg-red-100 dark:border-red-900 dark:text-red-300 dark:hover:bg-red-950/30"
      >
        <Trash2 className="w-3.5 h-3.5 mr-1.5"/>
        Remove {count} test term{count === 1 ? "" : "s"}
      </Button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="remove-test-glossary-dialog" className="max-w-md">
          <DialogHeader>
            <DialogTitle>Remove test glossary terms?</DialogTitle>
            <DialogDescription className="text-slate-600 dark:text-slate-400">
              This permanently deletes {preview?.count ?? 0} glossary term{preview?.count === 1 ? "" : "s"} matching the test fixture prefixes. This cannot be undone.
            </DialogDescription>
          </DialogHeader>
          {preview?.terms?.length > 0 && (
            <div className="max-h-48 overflow-y-auto text-sm border border-slate-200 dark:border-slate-800 rounded-md bg-slate-50 dark:bg-slate-900 p-2"
                 data-testid="remove-test-glossary-preview-list">
              <ul className="space-y-1">
                {preview.terms.map(t => (
                  <li key={t.term_id} className="flex items-center justify-between gap-2 text-slate-700 dark:text-slate-300">
                    <span className="truncate">{t.term}</span>
                    <span className="text-xs font-mono text-slate-400 dark:text-slate-500">{t.slug}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} disabled={busy}
                    className="border-slate-300 dark:border-slate-700 text-slate-700 dark:text-slate-300">
              Cancel
            </Button>
            <Button
              data-testid="remove-test-glossary-confirm"
              onClick={doDelete}
              disabled={busy}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              {busy && <Loader2 className="w-4 h-4 mr-2 animate-spin"/>}
              Yes, remove {preview?.count ?? 0}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default function GlossaryPanel({ glossary, refresh }) {
  const [term, setTerm] = useState("");
  const [partOfSpeech, setPartOfSpeech] = useState("");
  const [pronunciation, setPronunciation] = useState("");
  const [definitions, setDefinitions] = useState(["", "", ""]);
  const [hoverDefinition, setHoverDefinition] = useState("");
  const [hospiceContext, setHospiceContext] = useState("");
  const [usageCaution, setUsageCaution] = useState("");
  const [aliases, setAliases] = useState("");
  const [seeAlso, setSeeAlso] = useState("");
  const [sourceBlogIds, setSourceBlogIds] = useState("");
  const [sourceRefUrls, setSourceRefUrls] = useState("");
  const [visibility, setVisibility] = useState("public");
  const [hoverEnabled, setHoverEnabled] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [importing, setImporting] = useState(false);
  const [importReport, setImportReport] = useState(null);
  const fileInputRef = useRef(null);

  const reset = () => {
    setTerm(""); setPartOfSpeech(""); setPronunciation("");
    setDefinitions(["", "", ""]);
    setHoverDefinition(""); setHospiceContext(""); setUsageCaution("");
    setAliases(""); setSeeAlso("");
    setSourceBlogIds(""); setSourceRefUrls("");
    setVisibility("public"); setHoverEnabled(true);
    setEditingId(null);
  };
  const submit = async (e) => {
    e.preventDefault();
    if (!term.trim()) return;
    const cleanDefs = definitions.map(d => d.trim()).filter(Boolean);
    const body = {
      term,
      part_of_speech: partOfSpeech,
      pronunciation,
      definitions: cleanDefs,
      // Keep legacy `definition` populated for backward-compat with older readers.
      definition: cleanDefs[0] || "",
      hover_definition: hoverDefinition,
      hospice_context: hospiceContext,
      usage_caution: usageCaution,
      aliases: aliases.split(",").map(s=>s.trim()).filter(Boolean),
      see_also: seeAlso.split(",").map(s=>s.trim()).filter(Boolean),
      source_blog_ids: sourceBlogIds.split(",").map(s=>s.trim()).filter(Boolean),
      source_reference_urls: sourceRefUrls.split(",").map(s=>s.trim()).filter(Boolean),
      visibility,
      hover_enabled: hoverEnabled,
    };
    if (editingId) { await api.put(`/glossary/${editingId}`, body); toast.success("Term updated"); }
    else { await api.post("/glossary", body); toast.success("Term added"); }
    reset(); refresh();
  };
  const edit = (t) => {
    setEditingId(t.term_id);
    setTerm(t.term);
    setPartOfSpeech(t.part_of_speech || "");
    setPronunciation(t.pronunciation || "");
    const defs = (t.definitions && t.definitions.length > 0)
      ? t.definitions
      : ((t.definition || "").trim() ? [t.definition] : []);
    setDefinitions([defs[0] || "", defs[1] || "", defs[2] || ""]);
    setHoverDefinition(t.hover_definition || "");
    setHospiceContext(t.hospice_context || "");
    setUsageCaution(t.usage_caution || "");
    setAliases((t.aliases||[]).join(", "));
    setSeeAlso((t.see_also||[]).join(", "));
    setSourceBlogIds((t.source_blog_ids||[]).join(", "));
    setSourceRefUrls((t.source_reference_urls||[]).join(", "));
    setVisibility(t.visibility || "public");
    setHoverEnabled(t.hover_enabled !== false);
  };

  const handleImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImporting(true);
    setImportReport(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/glossary/import-csv", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 120000,
      });
      setImportReport(data);
      const parts = [];
      if (data.created) parts.push(`${data.created} added`);
      if (data.updated) parts.push(`${data.updated} updated`);
      if (data.skipped) parts.push(`${data.skipped} skipped`);
      toast.success(`CSV imported — ${parts.join(", ") || "no rows"}`, { duration: 6000 });
      if (data.total_errors > 0) toast.error(`${data.total_errors} row(s) had errors — see report below`);
      refresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || "CSV import failed");
    } finally {
      setImporting(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const handleExport = async () => {
    try {
      const res = await api.get("/glossary/export-csv", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      const cd = res.headers?.["content-disposition"] || "";
      const match = cd.match(/filename="?([^"]+)"?/);
      a.download = match ? match[1] : "hospice-glossary.csv";
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Export failed");
    }
  };
  const undoDelete = useUndoDelete();
  const [glossaryFilter, setGlossaryFilter] = useState("");
  // Filter matches term text, aliases, and definition (all case-insensitive).
  const filteredGlossary = useMemo(() => {
    const q = glossaryFilter.trim().toLowerCase();
    if (!q) return glossary;
    return glossary.filter(t =>
      (t.term || "").toLowerCase().includes(q) ||
      (t.definition || "").toLowerCase().includes(q) ||
      (t.aliases || []).some(a => (a || "").toLowerCase().includes(q))
    );
  }, [glossary, glossaryFilter]);
  const del = (t) => undoDelete.run({
    label: `Deleted term "${t.term}"`,
    deletePath: `/glossary/${t.term_id}`,
    restorePath: `/glossary/${t.term_id}/restore`,
    onDone: refresh,
  });

  /** Inline hover-toggle on each glossary row. PATCHes the term's
   * `hover_enabled` flag and surfaces a 5-second Undo toast so an accidental
   * click can be reverted. Matches the Undo pattern from Users → role change
   * and Users → un-mark test flag. */
  const toggleHoverEnabled = async (t, next) => {
    try {
      await api.put(`/glossary/${t.term_id}`, { hover_enabled: next });
      toast.success(
        next
          ? `Hover autolink turned ON for "${t.term}"`
          : `Hover autolink turned OFF for "${t.term}"`,
        {
          duration: 5000,
          action: {
            label: "Undo",
            onClick: async () => {
              try {
                await api.put(`/glossary/${t.term_id}`, { hover_enabled: !next });
                toast.success(`Hover autolink restored on "${t.term}"`);
                refresh();
              } catch (e) {
                toast.error(e?.response?.data?.detail || "Couldn't undo hover toggle");
              }
            },
          },
        },
      );
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't update hover toggle");
    }
  };

  return (
    <>
      <GlossarySettingsCard/>
      <Card className="border-slate-200 mt-6">
        <CardHeader><CardTitle className="font-heading">Glossary terms {editingId && <button onClick={reset} className="ml-3 text-xs uppercase tracking-widest text-slate-500">Cancel</button>}</CardTitle></CardHeader>
        <CardContent>
          <RemoveTestGlossaryBar onDeleted={refresh}/>
          <form onSubmit={submit} className="grid md:grid-cols-3 gap-3 items-end">
            <div><label className="text-xs uppercase tracking-widest text-slate-500 dark:text-slate-400">Term</label>
              <Input data-testid={ADMIN.glossaryTerm} value={term} onChange={e=>setTerm(e.target.value)}/></div>
            <div><label className="text-xs uppercase tracking-widest text-slate-500 dark:text-slate-400">Part of speech</label>
              <Input data-testid="admin-glossary-pos" value={partOfSpeech} onChange={e=>setPartOfSpeech(e.target.value)} placeholder="noun, verb, adj…"/></div>
            <div><label className="text-xs uppercase tracking-widest text-slate-500 dark:text-slate-400">Pronunciation</label>
              <Input data-testid="admin-glossary-pronunciation" value={pronunciation} onChange={e=>setPronunciation(e.target.value)} placeholder="HOS-pis"/></div>

            <div className="md:col-span-3">
              <label className="text-xs uppercase tracking-widest text-slate-500 dark:text-slate-400">Aliases <span className="text-slate-400 normal-case">(comma-sep, shown as "Synonyms of …")</span></label>
              <Input data-testid={ADMIN.glossaryAliases} value={aliases} onChange={e=>setAliases(e.target.value)} placeholder="e.g. DNR, no-code"/>
            </div>

            {/* Numbered definitions 1/2/3 */}
            {[0,1,2].map(i => (
              <div key={i} className="md:col-span-3">
                <label className="text-xs uppercase tracking-widest text-slate-500 dark:text-slate-400">
                  Definition {i + 1}
                  {i > 0 && <span className="text-slate-400 normal-case"> (optional)</span>}
                </label>
                <Textarea
                  data-testid={`admin-glossary-definition-${i + 1}`}
                  rows={2}
                  value={definitions[i]}
                  onChange={e => setDefinitions(prev => prev.map((d, j) => j === i ? e.target.value : d))}
                />
              </div>
            ))}

            <div className="md:col-span-3">
              <label className="text-xs uppercase tracking-widest text-slate-500 dark:text-slate-400">
                "Also" / Hospice context <span className="text-slate-400 normal-case">(short blurb shown after the last definition)</span>
              </label>
              <Textarea data-testid="admin-glossary-hospice-context" rows={2} value={hospiceContext} onChange={e=>setHospiceContext(e.target.value)}/>
            </div>

            <div className="md:col-span-3">
              <label className="text-xs uppercase tracking-widest text-slate-500 dark:text-slate-400">
                Usage / caution note <span className="text-slate-400 normal-case">(italic amber callout in the reader)</span>
              </label>
              <Textarea data-testid="admin-glossary-usage-caution" rows={2} value={usageCaution} onChange={e=>setUsageCaution(e.target.value)}/>
            </div>

            <div className="md:col-span-3">
              <label className="text-xs uppercase tracking-widest text-slate-500 dark:text-slate-400">
                See also <span className="text-slate-400 normal-case">(comma-sep related terms shown as chips)</span>
              </label>
              <Input data-testid="admin-glossary-see-also" value={seeAlso} onChange={e=>setSeeAlso(e.target.value)} placeholder="Palliative Care, Medicare Hospice Benefit"/>
            </div>

            {/* Visibility + hover toggle */}
            <div>
              <label className="text-xs uppercase tracking-widest text-slate-500 dark:text-slate-400">Visibility</label>
              <select
                data-testid="admin-glossary-visibility"
                value={visibility}
                onChange={e => setVisibility(e.target.value)}
                className="w-full mt-1 border border-slate-300 dark:border-slate-700 rounded-md px-3 py-2 text-sm bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100"
              >
                <option value="public">Public — shown on /glossary</option>
                <option value="hidden">Hidden — admin only</option>
                <option value="draft">Draft — admin only, work-in-progress</option>
              </select>
            </div>
            <div className="md:col-span-2 pt-4">
              <label className="inline-flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  data-testid="admin-glossary-hover-enabled"
                  checked={hoverEnabled}
                  onChange={() => setHoverEnabled(v => !v)}
                  className="w-4 h-4 accent-sky-700"
                />
                <span className="text-sm text-slate-800 dark:text-slate-200">
                  Autolink on blog pages
                  <span className="text-xs text-slate-500 dark:text-slate-400 block">
                    When on and a hover blurb is set, the term will be underlined + hoverable inside blog prose.
                  </span>
                </span>
              </label>
            </div>

            <div className="md:col-span-3">
              <label className="text-xs uppercase tracking-widest text-slate-500 dark:text-slate-400">
                Hover blurb <span className="text-slate-400 normal-case">(1-2 sentences shown on hover inside blogs. Leave blank to skip autolinking.)</span>
              </label>
              <Textarea
                data-testid="admin-glossary-hover-definition"
                rows={2}
                value={hoverDefinition}
                onChange={e=>setHoverDefinition(e.target.value)}
                placeholder="Short 1-2 sentence blurb — shown when a reader hovers this term inside a blog."
              />
            </div>

            <div className="md:col-span-3 grid md:grid-cols-2 gap-3">
              <div>
                <label className="text-xs uppercase tracking-widest text-slate-500 dark:text-slate-400">
                  Source blog IDs <span className="text-slate-400 normal-case">(comma-sep, e.g. HS-BLOG-025)</span>
                </label>
                <Input
                  data-testid="admin-glossary-source-blog-ids"
                  value={sourceBlogIds}
                  onChange={e=>setSourceBlogIds(e.target.value)}
                  placeholder="HS-BLOG-025, HS-BLOG-026"
                />
              </div>
              <div>
                <label className="text-xs uppercase tracking-widest text-slate-500 dark:text-slate-400">
                  Source reference URLs <span className="text-slate-400 normal-case">(comma-sep)</span>
                </label>
                <Input
                  data-testid="admin-glossary-source-reference-urls"
                  value={sourceRefUrls}
                  onChange={e=>setSourceRefUrls(e.target.value)}
                  placeholder="https://…, https://…"
                />
              </div>
            </div>

            <Button data-testid={ADMIN.glossarySubmit} type="submit" className="bg-sky-700 hover:bg-sky-600">{editingId ? "Save" : "Add term"}</Button>
          </form>

          {/* Bulk CSV controls */}
          <div className="mt-8 border-t border-slate-200 pt-6">
            <div className="flex items-center justify-between flex-wrap gap-3">
              <div>
                <div className="font-heading font-semibold text-slate-900">Bulk import / export</div>
                <p className="text-xs text-slate-500 mt-1 max-w-xl">
                  Import a spreadsheet of terms in one go. Rows are matched by term slug — existing terms update in place, new ones are added. Soft-deleted terms with the same slug revive.
                </p>
              </div>
              <div className="flex gap-2">
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".csv,text/csv"
                  onChange={handleImport}
                  className="hidden"
                  data-testid="admin-glossary-csv-input"
                />
                <Button
                  data-testid="admin-glossary-import-csv"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={importing}
                  variant="outline"
                  className="gap-2"
                >
                  <Upload className="w-4 h-4"/>
                  {importing ? "Importing…" : "Import CSV"}
                </Button>
                <Button
                  data-testid="admin-glossary-export-csv"
                  onClick={handleExport}
                  variant="outline"
                  className="gap-2"
                >
                  <Download className="w-4 h-4"/>
                  Export CSV
                </Button>
              </div>
            </div>
            {importReport && (
              <div
                data-testid="admin-glossary-import-report"
                className="mt-4 rounded-md border border-slate-200 bg-slate-50 p-4 text-sm"
              >
                <div className="flex flex-wrap gap-4">
                  <span><strong className="text-emerald-700">{importReport.created}</strong> added</span>
                  <span><strong className="text-sky-700">{importReport.updated}</strong> updated</span>
                  <span><strong className="text-slate-500">{importReport.skipped}</strong> skipped</span>
                  {importReport.total_errors > 0 && (
                    <span><strong className="text-red-600">{importReport.total_errors}</strong> errors</span>
                  )}
                </div>
                {importReport.errors?.length > 0 && (
                  <ul className="mt-2 text-xs text-red-700 list-disc list-inside space-y-0.5">
                    {importReport.errors.slice(0, 10).map((er, i) => (
                      <li key={i}>Row {er.row}: <em>{er.term}</em> — {er.error}</li>
                    ))}
                    {importReport.total_errors > importReport.errors.length && (
                      <li>…and {importReport.total_errors - importReport.errors.length} more.</li>
                    )}
                  </ul>
                )}
              </div>
            )}
          </div>

          {glossary.length > 0 && (
            <ListFilter
              value={glossaryFilter}
              onChange={setGlossaryFilter}
              placeholder="Filter terms by term, alias, or definition…"
              testId="admin-glossary-filter"
              className="mt-6"
            />
          )}
          <div className="text-xs uppercase tracking-widest text-slate-500 mt-4 mb-1">
            Terms ({filteredGlossary.length}{glossaryFilter && filteredGlossary.length !== glossary.length ? ` of ${glossary.length}` : ""})
          </div>
          <ul className="divide-y divide-slate-200 border-y border-slate-200 text-sm">
            {filteredGlossary.map(t => {
              const hasHover = !!(t.hover_definition || "").trim();
              return (
              <li key={t.term_id} className="py-3 flex items-start justify-between gap-4">
                <div className="flex-1">
                  <div className="font-heading font-semibold text-slate-900 flex items-center gap-2">
                    {t.term}
                    {hasHover && (
                      <span
                        data-testid={`admin-glossary-hover-badge-${t.term_id}`}
                        title="Has a hover definition — will show as a tooltip inside blogs."
                        className="text-[10px] font-bold uppercase tracking-widest bg-sky-100 text-sky-800 border border-sky-200 rounded-full px-1.5 py-0.5"
                      >
                        Hover
                      </span>
                    )}
                  </div>
                  {t.aliases?.length > 0 && <div className="text-xs text-slate-500">also: {t.aliases.join(", ")}</div>}
                  <div className="text-slate-600 text-sm mt-1 line-clamp-2">{t.definition}</div>
                  {hasHover && (
                    <div className="text-xs text-sky-700 mt-1 italic line-clamp-2">
                      Hover: {t.hover_definition}
                    </div>
                  )}
                  {(t.source_blog_ids?.length > 0 || t.source_reference_urls?.length > 0) && (
                    <div className="text-[11px] text-slate-500 mt-1 flex flex-wrap gap-x-3 gap-y-0.5">
                      {t.source_blog_ids?.length > 0 && (
                        <span data-testid={`admin-glossary-source-blog-ids-${t.term_id}`}>
                          <span className="uppercase tracking-widest text-slate-400">Blogs:</span>{" "}
                          {t.source_blog_ids.join(", ")}
                        </span>
                      )}
                      {t.source_reference_urls?.length > 0 && (
                        <span data-testid={`admin-glossary-source-refs-${t.term_id}`}>
                          <span className="uppercase tracking-widest text-slate-400">Refs:</span>{" "}
                          {t.source_reference_urls.length} link{t.source_reference_urls.length === 1 ? "" : "s"}
                        </span>
                      )}
                    </div>
                  )}
                </div>
                <div className="flex items-center gap-3">
                  <label
                    className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-widest text-slate-500 dark:text-slate-400 select-none"
                    title="Turn autolink on/off for this term inside blog prose"
                  >
                    <span>Autolink</span>
                    <Switch
                      data-testid={`admin-glossary-hover-row-${t.term_id}`}
                      checked={t.hover_enabled !== false}
                      onCheckedChange={(v) => toggleHoverEnabled(t, v)}
                      className="data-[state=checked]:bg-sky-600"
                    />
                  </label>
                  <button onClick={()=>edit(t)} className="text-sky-700 text-xs uppercase tracking-widest">Edit</button>
                  <button data-testid={`admin-glossary-delete-${t.term_id}`} onClick={()=>del(t)} title="Delete (with Undo)" className="text-red-500 hover:text-red-700"><Trash2 className="w-4 h-4"/></button>
                </div>
              </li>
              );
            })}
            {filteredGlossary.length === 0 && (
              <li className="py-6 text-slate-400 text-center">
                {glossary.length === 0 ? "No terms yet." : `No terms match "${glossaryFilter}".`}
              </li>
            )}
          </ul>
        </CardContent>
      </Card>
    </>
  );
}
